import { useState, useEffect, useMemo } from "react";
import ResponsiveBeforeAfter from "@/components/ResponsiveBeforeAfter";
import { Carousel, CarouselContent, CarouselItem, CarouselApi } from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { trackGalleryInteraction } from "@/lib/analytics";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface Transformation {
  before: string;
  after: string;
  petType: string;
  description: string;
  beforeAlt: string;
  afterAlt: string;
}

interface TransformationShowcaseProps {
  transformations: Transformation[];
}

const TransformationShowcase = ({ 
  transformations 
}: TransformationShowcaseProps) => {
  const [api, setApi] = useState<CarouselApi>();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showSwipeHint, setShowSwipeHint] = useState(true);
  const isMobile = useIsMobile();
  
  // Limit to first 4 transformations for hero carousel
  const carouselTransformations = transformations.slice(0, 4);
  const currentTransformation = carouselTransformations[currentIndex];
  
  // Auto-play plugin - only for mobile, with delay matching before/after cycle
  const plugins = useMemo(() => {
    if (isMobile) {
      return [Autoplay({
        delay: 5500,              // Wait for full before/after cycle (2.5s + 2.5s + buffer)
        stopOnInteraction: false, // Continue auto-rotating after user swipes
        stopOnMouseEnter: false,
      }) as any];
    }
    return [];
  }, [isMobile]);
  
  // Update current index when carousel changes
  useEffect(() => {
    if (!api) return;
    
    api.on("select", () => {
      setCurrentIndex(api.selectedScrollSnap());
      setShowSwipeHint(false); // Hide hint after first interaction
    });
  }, [api]);

  // Auto-hide swipe hint after 4 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSwipeHint(false);
    }, 4000);
    return () => clearTimeout(timer);
  }, []);
  
  return (
    <div className="relative">
      {/* Diagonal Background Element */}
      <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
        <svg
          className="absolute -top-20 -right-20 w-[600px] h-[600px] opacity-20"
          viewBox="0 0 600 600"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0 300 L300 0 L600 300 L300 600 Z"
            fill="url(#diagonal-gradient)"
          />
          <defs>
            <linearGradient id="diagonal-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.3" />
              <stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity="0.1" />
            </linearGradient>
          </defs>
        </svg>
      </div>
      
      {/* Auto-Play Carousel Hero Section */}
      <Carousel
        setApi={setApi}
        plugins={plugins}
        className="w-full"
        opts={{
          loop: true,
          duration: 30,
        }}
      >
        <CarouselContent>
          {carouselTransformations.map((transformation, index) => (
            <CarouselItem key={index}>
              <div className="relative h-[500px] md:h-[600px] lg:h-[700px] rounded-2xl overflow-hidden shadow-2xl">
                <ResponsiveBeforeAfter
                  before={transformation.before}
                  after={transformation.after}
                  beforeAlt={transformation.beforeAlt}
                  afterAlt={transformation.afterAlt}
                />
                
                {/* Overlay Content - Hidden on mobile for cleaner view */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-background/95 via-background/80 to-transparent p-6 md:p-10 pointer-events-none hidden md:block">
                  <div className="max-w-4xl mx-auto">
                    <div className="inline-block mb-3">
                      <span className="text-sm font-semibold text-primary bg-primary/10 px-3 py-1 rounded-full">
                        ✨ Transformation
                      </span>
                    </div>
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3">
                      {transformation.petType}
                    </h2>
                    <p className="text-base md:text-lg text-muted-foreground mb-6">
                      {transformation.description}
                    </p>
                    
                    {/* Counter */}
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-muted-foreground">
                        {currentIndex + 1} / {carouselTransformations.length}
                      </span>
                    </div>
                  </div>
                </div>
                
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        
        {/* Arrow Buttons */}
        <button
          onClick={() => {
            api?.scrollPrev();
            trackGalleryInteraction('carousel_click', 'prev', 'homepage_hero_arrow');
          }}
          className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 p-2 sm:p-3 rounded-full bg-background/80 backdrop-blur-sm border border-border/50 shadow-lg hover:bg-background transition-colors"
          aria-label="Previous transformation"
          data-gtm-id="home-transformation-carousel-prev-click"
        >
          <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 text-foreground" />
        </button>
        
        <button
          onClick={() => {
            api?.scrollNext();
            trackGalleryInteraction('carousel_click', 'next', 'homepage_hero_arrow');
          }}
          className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 p-2 sm:p-3 rounded-full bg-background/80 backdrop-blur-sm border border-border/50 shadow-lg hover:bg-background transition-colors"
          aria-label="Next transformation"
          data-gtm-id="home-transformation-carousel-next-click"
        >
          <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-foreground" />
        </button>

        {/* Dots Indicator - Uses padding for 24px+ touch target while keeping visual dot small */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 hidden md:flex gap-0 z-10">
          {carouselTransformations.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                api?.scrollTo(index);
                trackGalleryInteraction('carousel_click', carouselTransformations[index].petType, 'homepage_hero_dot');
              }}
              className="p-2 flex items-center justify-center"
              aria-label={`Go to transformation ${index + 1}`}
              data-gtm-id={`home-transformation-carousel-dot-${index}-click`}
            >
              <span 
                className={`block h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex 
                    ? 'w-8 bg-primary' 
                    : 'w-2 bg-muted-foreground/40 hover:bg-muted-foreground/60'
                }`}
              />
            </button>
          ))}
        </div>

        {/* Mobile Swipe Hint */}
        {showSwipeHint && (
          <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-10 md:hidden animate-fade-in">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-background/90 backdrop-blur-sm border border-border/50 shadow-lg">
              <span className="text-sm text-muted-foreground">Swipe to explore</span>
              <div className="animate-[swipe_1.5s_ease-in-out_infinite]">
                <ChevronRight className="w-4 h-4 text-primary" />
              </div>
            </div>
          </div>
        )}
      </Carousel>
    </div>
  );
};

export default TransformationShowcase;
